/**
 * Scale — an ordered set of pitches starting from a root.
 *
 * Scales are defined by their interval pattern from the root. The engine builds
 * any common scale by applying intervals; modes are computed by rotating the pattern.
 */

import { type Pitch } from "./pitch.js";
import { type Interval, parseInterval, transposeByInterval } from "./interval.js";

export interface Scale {
  root: Pitch;
  intervals: Interval[]; // Includes unison (P1) at index 0; does NOT include octave.
  name: string;
}

export type ScaleType =
  | "major"
  | "natural-minor"
  | "harmonic-minor"
  | "melodic-minor"
  | "dorian"
  | "phrygian"
  | "lydian"
  | "mixolydian"
  | "locrian"
  | "major-pentatonic"
  | "minor-pentatonic"
  | "blues"
  | "whole-tone"
  | "diminished-wh"   // whole-half (used over diminished chords)
  | "diminished-hw"   // half-whole (used over dominant 7♭9)
  | "harmonic-major"
  | "phrygian-dominant"
  | "hungarian-minor"
  | "double-harmonic"
  | "lydian-dominant"
  | "altered";

/** Scale interval patterns as quality+number shorthand. */
const SCALE_PATTERNS: Record<ScaleType, string[]> = {
  "major":             ["P1", "M2", "M3", "P4", "P5", "M6", "M7"],
  "natural-minor":     ["P1", "M2", "m3", "P4", "P5", "m6", "m7"],
  "harmonic-minor":    ["P1", "M2", "m3", "P4", "P5", "m6", "M7"],
  "melodic-minor":     ["P1", "M2", "m3", "P4", "P5", "M6", "M7"],
  "dorian":            ["P1", "M2", "m3", "P4", "P5", "M6", "m7"],
  "phrygian":          ["P1", "m2", "m3", "P4", "P5", "m6", "m7"],
  "lydian":            ["P1", "M2", "M3", "A4", "P5", "M6", "M7"],
  "mixolydian":        ["P1", "M2", "M3", "P4", "P5", "M6", "m7"],
  "locrian":           ["P1", "m2", "m3", "P4", "d5", "m6", "m7"],
  "major-pentatonic":  ["P1", "M2", "M3", "P5", "M6"],
  "minor-pentatonic":  ["P1", "m3", "P4", "P5", "m7"],
  "blues":             ["P1", "m3", "P4", "d5", "P5", "m7"],
  "whole-tone":        ["P1", "M2", "M3", "A4", "A5", "A6"],
  "diminished-wh":     ["P1", "M2", "m3", "P4", "d5", "m6", "M6", "M7"],
  "diminished-hw":     ["P1", "m2", "m3", "M3", "A4", "P5", "M6", "m7"],
  "harmonic-major":    ["P1", "M2", "M3", "P4", "P5", "m6", "M7"],
  "phrygian-dominant": ["P1", "m2", "M3", "P4", "P5", "m6", "m7"],
  "hungarian-minor":   ["P1", "M2", "m3", "A4", "P5", "m6", "M7"],
  "double-harmonic":   ["P1", "m2", "M3", "P4", "P5", "m6", "M7"],
  "lydian-dominant":   ["P1", "M2", "M3", "A4", "P5", "M6", "m7"],
  "altered":           ["P1", "m2", "m3", "d4", "d5", "m6", "m7"],
};

const DISPLAY_NAMES: Record<ScaleType, string> = {
  "major": "Major",
  "natural-minor": "Natural Minor",
  "harmonic-minor": "Harmonic Minor",
  "melodic-minor": "Melodic Minor",
  "dorian": "Dorian",
  "phrygian": "Phrygian",
  "lydian": "Lydian",
  "mixolydian": "Mixolydian",
  "locrian": "Locrian",
  "major-pentatonic": "Major Pentatonic",
  "minor-pentatonic": "Minor Pentatonic",
  "blues": "Blues",
  "whole-tone": "Whole Tone",
  "diminished-wh": "Diminished (W-H)",
  "diminished-hw": "Diminished (H-W)",
  "harmonic-major": "Harmonic Major",
  "phrygian-dominant": "Phrygian Dominant",
  "hungarian-minor": "Hungarian Minor",
  "double-harmonic": "Double Harmonic",
  "lydian-dominant": "Lydian Dominant",
  "altered": "Altered (Super Locrian)",
};

/** Build a scale from a root pitch and scale type. */
export function scale(root: Pitch, type: ScaleType): Scale {
  const pattern = SCALE_PATTERNS[type];
  const intervals = pattern.map((s) => {
    const iv = parseInterval(s);
    if (!iv) throw new Error(`Invalid interval in pattern: ${s}`);
    return iv;
  });
  return { root, intervals, name: DISPLAY_NAMES[type] };
}

/** Get the pitches of a scale, in order, starting from root. */
export function scalePitches(s: Scale): Pitch[] {
  return s.intervals.map((iv) => transposeByInterval(s.root, iv));
}

/**
 * Get the pitch at a scale degree (1-indexed). Out-of-range degrees wrap with octave shift.
 * Degree 8 = octave above root (= degree 1 + octave). Degree 9 = degree 2 + octave. Etc.
 */
export function scaleDegree(s: Scale, degree: number): Pitch {
  if (degree < 1) throw new Error(`Scale degree must be >= 1, got ${degree}`);
  const len = s.intervals.length;
  const idx = (degree - 1) % len;
  const octaveShift = Math.floor((degree - 1) / len);
  const base = s.intervals[idx]!;
  const shiftedSemitones = base.semitones + octaveShift * 12;
  return transposeByInterval(s.root, { number: base.number + octaveShift * 7, semitones: shiftedSemitones });
}

/**
 * Generate a mode by rotating the scale pattern.
 * For example, mode 2 of major = Dorian. Mode 5 of major = Mixolydian.
 *
 * Note: the resulting scale is named generically; for canonical mode names,
 * use scale(root, "dorian") etc. directly.
 */
export function modeOf(s: Scale, modeNumber: number): Scale {
  const len = s.intervals.length;
  if (modeNumber < 1 || modeNumber > len) {
    throw new Error(`Mode number must be 1..${len}, got ${modeNumber}`);
  }
  const newRoot = scaleDegree(s, modeNumber);
  const rotatedSemis: number[] = [];
  const rotatedNumbers: number[] = [];
  const baseSemi = s.intervals[modeNumber - 1]!.semitones;
  const baseNum = s.intervals[modeNumber - 1]!.number;
  for (let i = 0; i < len; i++) {
    const sourceIdx = (modeNumber - 1 + i) % len;
    const sourceInterval = s.intervals[sourceIdx]!;
    let semis = sourceInterval.semitones - baseSemi;
    let num = sourceInterval.number - baseNum;
    // Wrap to keep ascending.
    if (semis < 0) semis += 12;
    if (num < 0) num += 7;
    rotatedSemis.push(semis);
    rotatedNumbers.push(num + 1);
  }
  const intervals: Interval[] = rotatedSemis.map((semitones, i) => ({
    number: rotatedNumbers[i]!,
    semitones,
  }));
  return { root: newRoot, intervals, name: `${s.name} mode ${modeNumber}` };
}

/** Available scale types as a typed list (useful for UI dropdowns and exercise generators). */
export const ALL_SCALE_TYPES: ScaleType[] = Object.keys(SCALE_PATTERNS) as ScaleType[];
